/**
 * DiscoveryTripCard Component
 * Displays discovery trip information with rating and stats
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Badge } from 'react-bootstrap';
import { Heart, Eye, Share2, MapPin, Clock, Star } from 'lucide-react';
import { DiscoveryTrip } from '../../types/discovery.types';
import { socialService } from '../../services/api/socialService';
import './DiscoveryTripCard.css';

interface DiscoveryTripCardProps {
  trip: DiscoveryTrip;
  score?: number;
  reason?: string;
  badge?: React.ReactNode;
  showStats?: boolean;
}

export const DiscoveryTripCard: React.FC<DiscoveryTripCardProps> = ({
  trip,
  score,
  reason,
  badge,
  showStats = true
}) => {
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(trip.likes);

  const handleCardClick = () => {
    navigate(`/trips/${trip.originalId}`);
  };

  const handleLikeToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      if (isLiked) {
        await socialService.unlikeEntity('TRIP', trip.originalId);
        setIsLiked(false);
        setLikesCount((prev) => prev - 1);
      } else {
        await socialService.likeEntity('TRIP', trip.originalId);
        setIsLiked(true);
        setLikesCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      if (navigator.share) {
        await navigator.share({
          title: trip.title,
          text: trip.description || '',
          url: window.location.origin + `/trips/${trip.originalId}`
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(
          window.location.origin + `/trips/${trip.originalId}`
        );
        // TODO: Show toast notification
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Failed to share:', error);
    }
  };

  const formatDuration = (days: number): string => {
    if (days === 1) return '1 day';
    if (days < 7) return `${days} days`;
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    if (remainingDays === 0) return weeks === 1 ? '1 week' : `${weeks} weeks`;
    return `${weeks}w ${remainingDays}d`;
  };

  const getDestinationsDisplay = (): string => {
    const destinations = trip.destinations.slice(0, 2);
    const names = destinations.map((d) => d.name).join(', ');
    if (trip.destinations.length > 2) {
      return `${names} +${trip.destinations.length - 2}`;
    }
    return names;
  };

  return (
    <Card
      className='discovery-trip-card'
      onClick={handleCardClick}
    >
      {badge && <div className='discovery-trip-card-badge'>{badge}</div>}

      <div className='discovery-trip-card-image-container'>
        <div className='discovery-trip-card-image-placeholder'>
          <MapPin size={48} />
        </div>
        {score !== undefined && (
          <div className='discovery-trip-card-score'>
            <Star
              size={14}
              fill='#ffc107'
              stroke='#ffc107'
            />
            <span>{score.toFixed(1)}</span>
          </div>
        )}
      </div>

      <Card.Body className='discovery-trip-card-body'>
        <Card.Title className='discovery-trip-card-title'>
          {trip.title}
        </Card.Title>

        {trip.description && (
          <Card.Text className='discovery-trip-card-description'>
            {trip.description}
          </Card.Text>
        )}

        <div className='discovery-trip-card-meta'>
          <div className='discovery-trip-card-meta-item'>
            <MapPin size={16} />
            <span>{getDestinationsDisplay()}</span>
          </div>
          <div className='discovery-trip-card-meta-item'>
            <Clock size={16} />
            <span>{formatDuration(trip.duration)}</span>
          </div>
          {trip.rating > 0 && (
            <div className='discovery-trip-card-meta-item'>
              <Star
                size={16}
                fill='#ffc107'
                stroke='#ffc107'
              />
              <span>{trip.rating.toFixed(1)}</span>
            </div>
          )}
        </div>

        {trip.tags.length > 0 && (
          <div className='discovery-trip-card-tags'>
            {trip.tags.slice(0, 3).map((tag, index) => (
              <Badge
                key={index}
                bg='secondary'
                className='discovery-trip-card-tag'
              >
                {tag}
              </Badge>
            ))}
            {trip.tags.length > 3 && (
              <Badge
                bg='light'
                text='dark'
                className='discovery-trip-card-tag'
              >
                +{trip.tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        {reason && (
          <div className='discovery-trip-card-reason'>
            <em>{reason}</em>
          </div>
        )}

        {showStats && (
          <div className='discovery-trip-card-stats'>
            <button
              className={`discovery-trip-card-stat-btn ${
                isLiked ? 'active' : ''
              }`}
              onClick={handleLikeToggle}
            >
              <Heart
                size={18}
                fill={isLiked ? 'currentColor' : 'none'}
              />
              <span>{likesCount}</span>
            </button>
            <div className='discovery-trip-card-stat'>
              <Eye size={18} />
              <span>{trip.viewCount}</span>
            </div>
            <button
              className='discovery-trip-card-stat-btn'
              onClick={handleShare}
            >
              <Share2 size={18} />
              <span>{trip.shareCount}</span>
            </button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
